import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/clerk';
import { keepAppShell, SIGN_OUT_HOLD_MS, signOutStillHeld } from '../../utils/userLoad';

/**
 * Protected route component that redirects unauthenticated users to sign-in.
 *
 * Shows a loading state while Clerk is initializing.
 */
function ProtectedRoute({ children }) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const shellShown = useRef(false);
  const heldSince = useRef(null);
  const [, setHoldTick] = useState(0);
  if (isLoaded && isSignedIn) {
    shellShown.current = true;
    heldSince.current = null;
  }
  const signedOut = Boolean(isLoaded && !isSignedIn);
  if (signedOut && shellShown.current && heldSince.current == null) {
    heldSince.current = Date.now();
  }
  const hold = signOutStillHeld({
    shellShown: shellShown.current,
    signedOut,
    heldSince: heldSince.current,
  });

  useEffect(() => {
    if (!hold) return undefined;
    const timer = setTimeout(() => setHoldTick((tick) => tick + 1), SIGN_OUT_HOLD_MS);
    return () => clearTimeout(timer);
  }, [hold]);

  // Show loading state while Clerk initializes.
  // After the first signed-in paint, a flicker must not unmount the app.
  if (!keepAppShell({ ready: isLoaded, shellShown: shellShown.current })) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // A one-frame signed-out blip used to navigate away and remount Write
  // mid-reply. Leave only after it lasts.
  if (signedOut && !hold) {
    shellShown.current = false;
    heldSince.current = null;
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  return children;
}

export default ProtectedRoute;
